import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import axios from 'src/app/Service/axios';
import { DataService } from 'src/app/Service/data.service';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { MapService } from 'src/app/Service/map.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { ChartContext, ChartDataItem } from 'src/app/shared/chart/chart.models';
import Map from 'ol/Map';
import View from 'ol/View';
import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import { Tile as TileLayer } from 'ol/layer';
import { Cluster, OSM, Vector, XYZ } from 'ol/source';
import { fromLonLat } from 'ol/proj';
import { Circle, Fill, Stroke, Style, Text } from 'ol/style';
import AnimatedCluster from 'ol-ext/layer/AnimatedCluster';
import SelectCluster from 'ol-ext/interaction/SelectCluster';
import momentJalaali from 'moment-jalaali';
import { DashboardChartVehiclesModalComponent } from './components/chart-vehicles-modal/chart-vehicles-modal.component';
import { DashboardClusterDetailsModalComponent } from './components/cluster-details-modal/cluster-details-modal.component';
import { DashboardChartKey, DashboardMapPoint, ServiceAreaOption, VehicleTypeOption, ViolationTypeOption } from './dashbord.models';

declare let $: any;

@Component({
  selector: 'dashbord',
  templateUrl: './dashbord.component.html',
  styleUrls: ['./dashbord.component.css'],
})
export class DashbordComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DashboardChartVehiclesModalComponent) private chartVehiclesModal!: DashboardChartVehiclesModalComponent;
  @ViewChild(DashboardClusterDetailsModalComponent) private clusterDetailsModal!: DashboardClusterDetailsModalComponent;

  constructor(
    private notifyService: NotificationService,
    private router: Router,
    private otherService: OtherService,
    private mapService: MapService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private dataService: DataService,
    private guardService: GurdServiceService,
  ) {}

  readonly startDateForm: FormGroup = this.formBuilder.group({
    date: momentJalaali().subtract(1, 'jMonth').valueOf(),
  });
  readonly endDateForm: FormGroup = this.formBuilder.group({ date: Date.now() });
  readonly startTimeControl = new FormControl('00:00');
  readonly endTimeControl = new FormControl('23:59');
  readonly serviceAreaControl = new FormControl(-1);
  readonly vehicleTypeControl = new FormControl(-1);
  filtersOpen = this.dataService.dashbordData?.filtersOpen ?? true;
  isRentalDashboard = false;
  activeVehicleCount = 0;
  totalVehicleCount = 0;
  serviceAreas: ServiceAreaOption[] = [];
  vehicleTypes: VehicleTypeOption[] = [];
  violationTypes: ViolationTypeOption[] = [];

  private serviceAreaId = -1;
  private vehicleTypeId = -1;
  private vehicleMap?: Map;
  private violationMap?: Map;
  private initializationTimer?: ReturnType<typeof setTimeout>;
  private refreshTimer?: ReturnType<typeof setTimeout>;
  private destroyed = false;

  private readonly vehicleChartColors = [
    'rgb(255, 99, 132)', 'rgb(255, 159, 64)', 'rgb(255, 205, 86)',
    'rgb(75, 192, 192)', 'rgb(54, 162, 235)', 'rgb(153, 102, 255)', 'rgb(201, 203, 207)',
  ];
  private readonly trafficChartColors = [
    'rgb(75, 192, 192)', 'rgb(153, 102, 255)', 'rgb(54, 162, 235)',
    'rgb(255, 159, 64)', 'rgb(255, 205, 86)', 'rgb(255, 99, 132)', 'rgb(201, 203, 207)',
  ];
  private readonly statusChartColors = [
    'rgb(111, 255, 111)', 'rgb(255, 153, 204)', 'rgb(29, 179, 214)',
    'rgb(45, 103, 103)', 'rgb(251, 101, 150)', 'rgb(36, 123, 46)', 'rgb(103, 13, 183)',
  ];

  vehicleTrafficChart: ChartContext = {
    type: 'bar',
    title: 'پیک ترافیک روزانه (km)',
    colors: this.trafficChartColors,
    data: [],
    loading: true,
  };

  vehicleTypeChart: ChartContext = {
    type: 'bar',
    title: 'تعداد وسیله نقلیه به تفکیک نوع',
    colors: this.statusChartColors,
    data: [],
    zoom: true,
    loading: true,
  };

  highRiskDriversChart: ChartContext = {
    type: 'bar',
    title: 'رانندگان پر خطر',
    colors: this.statusChartColors,
    data: [],
    zoom: true,
    loading: true,
  };

  vehicleManufactureYearChart: ChartContext = {
    type: 'bar',
    title: 'تعداد خودرو براساس سال ساخت',
    colors: this.vehicleChartColors,
    data: [],
    zoom: true,
    loading: true,
  };

  mostViolatingVehiclesChart: ChartContext = {
    type: 'bar',
    title: 'پر تخلفترین وسایل (تعداد تخلف)',
    colors: this.statusChartColors,
    data: [],
    zoom: true,
    loading: true,
  };

  vehicleAreaServiceChart: ChartContext = {
    type: 'bar',
    zoom: true,
    title: 'تعداد خودرو به تفکیک حوزه خدمت',
    colors: this.vehicleChartColors,
    data: [],
    loading: true,
  };

  vehicleStatusChart: ChartContext = {
    type: 'pie',
    title: 'وضعیت خودروها',
    data: [],
    loading: true,
  };

  vehicleGroupChart: ChartContext = {
    type: 'pie',
    title: 'تعداد وسیله نقلیه به تفکیک گروه (تعداد)',
    colors: this.vehicleChartColors,
    data: [],
    loading: true,
  };

  vehicleViolationChart: ChartContext = {
    type: 'pie',
    title: 'تخلف و وقایع (تعداد تخلف)',
    colors: this.trafficChartColors,
    data: [],
    loading: true,
  };

  vehicleActivityChart: ChartContext = {
    type: 'gauge',
    title: 'تعداد فعال به کل',
    data: [],
    max: 0,
    loading: true,
  };

  ngOnInit(): void {
    this.isRentalDashboard = this.route.snapshot.url.some(segment => segment.path === 'rent-dashboard');
    if (!this.isRentalDashboard) this.filtersOpen = false;
    const savedFilters = this.dataService.dashbordData;
    if (savedFilters) {
      this.serviceAreaId = Number(savedFilters.iMobileDomain ?? -1);
      this.vehicleTypeId = Number(savedFilters.tiMobileType ?? -1);
      this.serviceAreaControl.setValue(this.serviceAreaId);
      this.vehicleTypeControl.setValue(this.vehicleTypeId);
      this.startDateForm.controls.date.setValue(savedFilters.dataFrom ?? this.startDateForm.controls.date.value);
      this.endDateForm.controls.date.setValue(savedFilters.dataTo ?? this.endDateForm.controls.date.value);
      this.startTimeControl.setValue(savedFilters.stime ?? '00:00');
      this.endTimeControl.setValue(savedFilters.etime ?? '23:59');
    }
    if (this.isRentalDashboard) {
      this.vehicleStatusChart = { ...this.vehicleStatusChart, title: 'وضعیت دستگاه ها' };
      this.vehicleAreaServiceChart = { ...this.vehicleAreaServiceChart, title: 'تعداد خودرو به تفکیک سایت' };
      this.vehicleTrafficChart = { ...this.vehicleTrafficChart, title: 'پیک زمانی اجاره' };
    }
  }

  ngAfterViewInit(): void {
    $('.clockpicker').clockpicker({ donetext: 'top', align: 'top', autoclose: true });
    // Start after the first view check so chart input changes are safe.
    this.initializationTimer = setTimeout(() => void this.loadDashboard(), 0);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.initializationTimer);
    clearTimeout(this.refreshTimer);
    for (const map of [this.vehicleMap, this.violationMap]) {
      map?.setTarget(undefined);
      map?.dispose();
    }
  }

  private async loadDashboard(): Promise<void> {
    this.otherService.Showspinner();
    try {
      if (this.isRentalDashboard) {
        this.vehicleMap = this.createMap('vehicleLocationsMap');
        this.violationMap = this.createMap('violationLocationsMap');
      }
      const results = await Promise.allSettled([
        this.loadServiceAreas(),
        this.loadCategoryChart('vehicleStatusChart', 'Report_VehicleStatus', '57'),
        this.loadCategoryChart('vehicleAreaServiceChart', 'Report_MobileCountBaseMobileDomain', '45'),
        this.loadVehicleActivityChart(),
        this.loadCategoryChart('vehicleTrafficChart', 'Report_DailyTraffic', '46', true),
        this.loadCategoryChart('vehicleViolationChart', 'Report_Journal', '59'),
        ...(this.isRentalDashboard ? [this.loadVehicleLocations(), this.loadViolationTypes()] : [
          this.loadVehicleTypes(),
          this.loadCategoryChart('vehicleGroupChart', 'Report_MobileType', '58'),
          this.loadCategoryChart('vehicleTypeChart', 'Report_GPSType', '47'),
          this.loadCategoryChart('vehicleManufactureYearChart', 'Report_YearConstruction', '52'),
          this.loadCategoryChart('highRiskDriversChart', 'Report_RedDriving', '50'),
          this.loadCategoryChart('mostViolatingVehiclesChart', 'Report_MostSerious', '54'),
        ]),
      ]);
      if (!this.destroyed && results.some(result => result.status === 'rejected')) {
        this.notifyService.showError('دریافت بخشی از اطلاعات داشبورد با خطا مواجه شد. دوباره تلاش کنید.');
      }
    } catch {
      this.notifyService.showError('دریافت اطلاعات داشبورد با خطا مواجه شد. دوباره تلاش کنید.');
    } finally {
      this.otherService.nonespinner();
    }
  }

  applyFilters(): void {
    this.filtersOpen = false;
    this.dataService.dashbordData = {
      filtersOpen: false,
      dataFrom: this.startDateForm.controls.date.value,
      dataTo: this.endDateForm.controls.date.value,
      stime: $('#Stime').val(),
      etime: $('#Etime').val(),
      iMobileDomain: this.serviceAreaControl.value,
      tiMobileType: this.vehicleTypeControl.value,
    };
    // Let the filter collapse finish before recreating the page.
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      const currentRoute = this.router.url.split('?')[0];
      void this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => this.router.navigateByUrl(currentRoute));
    }, 350);
  }

  goBack(): void {
    void this.router.navigateByUrl('/');
  }

  showHelp(): void {
    this.otherService.guid(39);
  }

  private createReportModel(withDateRange = false) {
    return {
      iSystemUser: this.guardService.decrypt(localStorage.getItem('ID')!),
      strSession: this.guardService.decrypt(localStorage.getItem('Session')!),
      iMobileDomain: this.serviceAreaId,
      tiMobileType: this.vehicleTypeId,
      ...(withDateRange ? {
        strSDate: this.startDateForm.controls.date.value,
        strEDate: this.endDateForm.controls.date.value,
        strSTime: $('#Stime').val(),
        strETime: $('#Etime').val(),
      } : {}),
    };
  }

  private async loadVehicleActivityChart(): Promise<void> {
    this.vehicleActivityChart = { ...this.vehicleActivityChart, loading: true, error: undefined };
    try {
      const model = this.createReportModel();
      const response = await axios.post('Report_VehicleStatusGatch', model);
      const responseData = JSON.parse(response.data);
      if (responseData.iResult == 0) {
        this.vehicleActivityChart = {
          ...this.vehicleActivityChart,
          error: responseData.strError || 'دریافت اطلاعات نمودار با خطا مواجه شد.',
        };
        this.notifyService.showError(responseData.strError);
        return;
      }
      if (responseData.ISSessionExpired == true) {
        this.notifyService.showError('لطفا وارد سیستم شوید');
        this.otherService.removeLocalStorage();
        this.router.navigate(['login'], { queryParams: { error: '56' } });
        return;
      }
      if (Array.isArray(responseData) && responseData.length === 0) {
        this.activeVehicleCount = 0;
        this.totalVehicleCount = 0;
        this.vehicleActivityChart = { ...this.vehicleActivityChart, type: 'gauge', max: 0, data: [] };
        return;
      }
      const gauge = Array.isArray(responseData) ? responseData[0] : undefined;
      if (!gauge || !this.isNonNegativeChartValue(gauge.CurrentValue) || !this.isNonNegativeChartValue(gauge.Maximume)) {
        throw new Error('Invalid vehicle activity data');
      }
      this.activeVehicleCount = Number(gauge.CurrentValue);
      this.totalVehicleCount = Number(gauge.Maximume);
      this.vehicleActivityChart = {
        ...this.vehicleActivityChart,
        type: 'gauge',
        max: this.totalVehicleCount,
        data: [{ name: 'تعداد خودرو فعال', value: this.activeVehicleCount }],
      };
    } catch (error) {
      this.vehicleActivityChart = {
        ...this.vehicleActivityChart,
        error: 'دریافت اطلاعات نمودار با خطا مواجه شد. دوباره به روز رسانی کنید.',
      };
      throw error;
    } finally {
      this.vehicleActivityChart = { ...this.vehicleActivityChart, loading: false };
    }
  }

  private async loadCategoryChart(
    chartKey: DashboardChartKey,
    api: string,
    sessionError: string,
    withDateRange = false,
  ): Promise<void> {
    this[chartKey] = { ...this[chartKey], loading: true, error: undefined };
    try {
      const model = this.createReportModel(withDateRange);
      const response = await axios.post(api, model);
      const responseData = JSON.parse(response.data);
      if (responseData.iResult == 0) {
        this[chartKey] = { ...this[chartKey], error: responseData.strError || 'دریافت اطلاعات نمودار با خطا مواجه شد.' };
        this.notifyService.showError(responseData.strError);
        return;
      }
      if (responseData.ISSessionExpired == true) {
        this.notifyService.showError('لطفا وارد سیستم شوید');
        this.otherService.removeLocalStorage();
        this.router.navigate(['login'], { queryParams: { error: sessionError } });
        return;
      }
      if (!Array.isArray(responseData) || responseData.some(item =>
        !item || !(typeof item.X === 'string' || (typeof item.X === 'number' && Number.isFinite(item.X))) || !this.isNonNegativeChartValue(item.Y)
      )) {
        throw new Error('Invalid category chart data');
      }
      this[chartKey] = {
        ...this[chartKey],
        data: responseData.map(item => ({ name: String(item.X), value: Number(item.Y) })),
      };
    } catch (error) {
      this[chartKey] = {
        ...this[chartKey],
        error: 'دریافت اطلاعات نمودار با خطا مواجه شد. دوباره به روز رسانی کنید.',
      };
      throw error;
    } finally {
      this[chartKey] = { ...this[chartKey], loading: false };
    }
  }

  private isNonNegativeChartValue(value: unknown): boolean {
    return (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')) &&
      Number.isFinite(Number(value)) && Number(value) >= 0;
  }

  onVehicleStatusClick(item: ChartDataItem): void {
    void this.openChartVehicles(1, item.name, 'نمودارد وضعیت خودروها.xlsx');
  }

  onServiceAreaClick(item: ChartDataItem): void {
    void this.openChartVehicles(2, item.name, 'نمودار خودرو به تفکیک حوزه.xlsx');
  }

  onVehicleTypeClick(item: ChartDataItem): void {
    void this.openChartVehicles(4, item.name, 'وسایل نقلیه به تفکیک نوع.xlsx');
  }

  onVehicleGroupClick(item: ChartDataItem): void {
    void this.openChartVehicles(5, item.name, 'وسایل نقلیه به تفکیک گروه.xlsx');
  }

  onManufactureYearClick(item: ChartDataItem): void {
    void this.openChartVehicles(7, item.name, 'سال ساخت.xlsx');
  }

  private async openChartVehicles(chartType: number, categoryName: string, fileName: string): Promise<void> {
    this.otherService.Showspinner();
    try {
      const response = await axios.post('GetMobileListBaseChartStatus', {
        ...this.createReportModel(), tiChart: chartType, strValue: categoryName,
      });
      const vehicles = JSON.parse(response.data);
      if (this.handleReportError(vehicles)) return;
      if (!Array.isArray(vehicles)) throw new Error('Invalid chart vehicle list');
      if (!this.destroyed) this.chartVehiclesModal.open(vehicles, fileName);
    } catch {
      this.notifyService.showError('دریافت فهرست خودروها با خطا مواجه شد. دوباره تلاش کنید.');
    } finally {
      this.otherService.nonespinner();
    }
  }

  private async loadServiceAreas(): Promise<void> {
    const userId = this.guardService.decrypt(localStorage.getItem('ID')!);
    const response = await axios.post(`GetMobileDomain4Report?iSystemUser=${userId}`, {});
    const areas = JSON.parse(response.data);
    if (!Array.isArray(areas)) throw new Error('Invalid service area options');
    this.serviceAreas = areas;
  }

  private async loadVehicleTypes(): Promise<void> {
    const response = await axios.post('GetMobileType', {});
    if (!Array.isArray(response.data)) throw new Error('Invalid vehicle type options');
    this.vehicleTypes = response.data;
  }

  private handleReportError(response: any, sessionError?: string): boolean {
    if (response?.iResult == 0) {
      this.notifyService.showError(response.strError);
      return true;
    }
    if (response?.ISSessionExpired == true) {
      this.notifyService.showError('لطفا وارد سیستم شوید');
      this.otherService.removeLocalStorage();
      void this.router.navigate(['login'], sessionError ? { queryParams: { error: sessionError } } : {});
      return true;
    }
    return false;
  }

  private async loadVehicleLocations(): Promise<void> {
    const response = await axios.post('Report_MobileCluster', this.createReportModel());
    const locations = JSON.parse(response.data);
    if (this.handleReportError(locations, '64')) return;
    if (!Array.isArray(locations)) throw new Error('Invalid vehicle locations');
    this.renderMapClusters(this.vehicleMap, locations, false);
  }

  private async loadViolationTypes(): Promise<void> {
    const model = this.createReportModel();
    const response = await axios.post('GetJournalType', {
      iSystemUser: model.iSystemUser, strSession: model.strSession,
    });
    const types = JSON.parse(response.data);
    if (this.handleReportError(types, '66')) return;
    if (!Array.isArray(types)) throw new Error('Invalid violation types');
    this.violationTypes = types;
    if (types.length > 0 && !this.destroyed) await this.loadViolationLocations(types[0].tiJournalType);
  }

  async onViolationTypeChange(event: Event): Promise<void> {
    const select = event.target as HTMLSelectElement;
    this.otherService.Showspinner();
    try {
      await this.loadViolationLocations(select.value);
    } catch {
      this.notifyService.showError('دریافت موقعیت تخلفات با خطا مواجه شد. دوباره تلاش کنید.');
    } finally {
      this.otherService.nonespinner();
    }
  }

  private async loadViolationLocations(violationType: number | string): Promise<void> {
    const response = await axios.post('Report_GeoJournal', {
      ...this.createReportModel(), tiJournalType: violationType,
    });
    const locations = JSON.parse(response.data);
    if (this.handleReportError(locations, '66')) return;
    if (!Array.isArray(locations)) throw new Error('Invalid violation locations');
    this.renderMapClusters(this.violationMap, locations, true);
  }

  private createMap(target: string): Map {
    const settings = window as Window & { bOfflineMap?: boolean; strMapUrl?: string };
    const map = new Map({
      target,
      view: new View({ zoom: 4, center: fromLonLat([51.3833554, 35.6770113]) }),
      layers: [new TileLayer({
        source: settings.bOfflineMap
          ? new XYZ({ url: settings.strMapUrl, attributions: 'Map data © RayanAndishehNasr' })
          : new OSM(),
      })],
    });
    map.on('click', event => {
      const hit = this.mapService.mapPointerMove(map.getEventPixel(event.originalEvent), map);
      const features = hit.featureLayer?.feature.get('features');
      const vehicle = features?.length === 1 ? features[0].get('data') : undefined;
      if (vehicle?.strFullName) this.clusterDetailsModal.open(vehicle);
    });
    return map;
  }

  private renderMapClusters(map: Map | undefined, locations: DashboardMapPoint[], isViolation: boolean): void {
    if (!map || this.destroyed) return;
    this.mapService.removeLayerByName('Cluster', map);
    const previousSelection = map.getInteractions().getArray().find(interaction => interaction instanceof SelectCluster);
    if (previousSelection) map.removeInteraction(previousSelection);
    const features = locations.map((location, index) => {
      const feature = new Feature({
        type: 'removable',
        geometry: new Point(this.mapService.transformCoordinates2(isViolation
          ? [location.fLon, location.fLat] : [location.dLongitude, location.dLatitude])),
        id_: location.strUnitId,
        data: { ...location, Id: location.strUnitId },
      });
      feature.set('id', index);
      return feature;
    });
    const clusterSource = new Cluster({ distance: 40, source: new Vector({ features }) });
    const styleCache: Record<number, Style> = {};
    const clusterStyle = (feature: Feature): Style => {
      const size = feature.get('features').length;
      if (!styleCache[size]) {
        const color = size > 25 ? '192,0,0' : size > 8 ? '255,128,0' : '0,128,0';
        const radius = Math.max(8, Math.min(size * 0.75, 20));
        const dash = (2 * Math.PI * radius) / 6;
        styleCache[size] = new Style({
          image: new Circle({
            radius,
            stroke: new Stroke({ color: `rgba(${color},0.5)`, width: 15, lineDash: [0, dash, dash, dash, dash, dash, dash], lineCap: 'butt' }),
            fill: new Fill({ color: `rgba(${color},1)` }),
          }),
          text: new Text({ text: String(size), fill: new Fill({ color: '#fff' }) }),
        });
      }
      return styleCache[size];
    };
    map.addLayer(new AnimatedCluster({ name: 'Cluster', source: clusterSource, animationDuration: 700, style: clusterStyle } as any));
    const selectedStyle = new Style({
      image: new Circle({ radius: 5, stroke: new Stroke({ color: 'rgba(128,0,0,0.8)', width: 2 }), fill: new Fill({ color: 'rgba(128,0,0,0.8)' }) }),
      stroke: new Stroke({ color: '#fff', width: 1 }),
    });
    const singleStyle = new Style({
      image: new Circle({ radius: 5, stroke: new Stroke({ color: 'rgba(0,0,192,0.5)', width: 2 }), fill: new Fill({ color: 'rgba(0,0,192,0.3)' }) }),
    });
    map.addInteraction(new SelectCluster({
      pointRadius: 7,
      animate: true,
      featureStyle: () => [selectedStyle],
      style: feature => feature.get('features').length > 1 ? clusterStyle(feature as Feature) : [singleStyle],
    }));
  }

}
